"use client";

import { useSession as useNextAuthSession } from "next-auth/react";
import { useEffect, useState } from "react";

import type { CachedSession } from "@/lib/storage";
import { loadCachedSession, saveCachedSession } from "@/lib/storage";

/**
 * Enhanced session hook with localStorage caching
 *
 * Eliminates flash of logged-out state by:
 * 1. Loading cached session immediately on mount (synchronous)
 * 2. Fetching fresh session in background
 * 3. Updating cache when session changes
 *
 * Provides isAuthenticated and isPremium for convenience
 */
export function useSession() {
  const nextAuthSession = useNextAuthSession();
  const [cachedData, setCachedData] = useState<CachedSession | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  // Hydrate cached session on mount (client-side only)
  useEffect(() => {
    const cached = loadCachedSession();
    setCachedData(cached);
    setHasHydrated(true);
  }, []);

  // Update cache whenever NextAuth session changes
  useEffect(() => {
    if (nextAuthSession.status !== "loading" && nextAuthSession.data) {
      const cacheData: CachedSession = {
        user: nextAuthSession.data.user as any,
        expires: nextAuthSession.data.expires,
        cachedAt: Date.now(),
      };
      saveCachedSession(cacheData);
      setCachedData(cacheData);
    } else if (nextAuthSession.status === "unauthenticated") {
      // Clear cache on logout
      saveCachedSession(null);
      setCachedData(null);
    }
  }, [nextAuthSession.data, nextAuthSession.status]);

  // Determine which data to use:
  // - If NextAuth has loaded, use it (source of truth)
  // - Otherwise, use cached data if available (optimistic)
  const effectiveData =
    nextAuthSession.status !== "loading" && nextAuthSession.data
      ? nextAuthSession.data
      : hasHydrated && cachedData
        ? (cachedData as any)
        : null;

  const isAuthenticated = !!effectiveData?.user?.id;
  const isPremium = !!(effectiveData?.user as any)?.premiumTier;

  // Consider loading only if:
  // - NextAuth is loading AND
  // - We don't have cached data to show
  const isLoading = nextAuthSession.status === "loading" && !hasHydrated;

  return {
    ...nextAuthSession,
    data: effectiveData,
    isAuthenticated,
    isPremium,
    isLoading,
  };
}
